import React, { useCallback, useEffect, useState, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { DocumentUploadCard } from "../ui/DocumentUploadCard";
import { UploadedFiles } from "../UploadedFiles";
import { InvalidRequirementReason } from "../InvalidRequirementReason";
import { CancelToReview } from "../CancelToReview";
import { FormValue, Status } from "../../../redux/interfaces/folder.types";
import { DocumentExamples } from "./DocumentExamples";
import { DocumentTypeSelector } from "../ui/DocumentTypeSelector";
import { labelPredialOptions } from "./fixtures/predialTypes";
import {
  predialOrScripturesTypes,
  labelPredialOrScripturesOptions,
} from "./fixtures/predialOrScripturesTypes";
import { toast } from "sonner";
import { uploadFileRequirement } from "../../../utils/vault/uploadFileRequirement";
import { useUploadFileToVault } from "../../../hooks/vault/mutations/useUploadFileToVault";
import { useUpdateVaultFormValues } from "../../../hooks/vault/mutations/useUpdateVaultFormValues";
import type { UploadFile } from "antd/es/upload/interface";
import {
  LARGE_DOCUMENT_MAX_FILE_SIZE_MB,
  PREDIAL_MAX_FILE_SIZE_MB,
} from "../../../utils/vault/verificationUploadLimits";
import { UploadProgress } from "../ui/UploadProgress";

interface Props {
  type: "collateral" | "property";
  doc: "predial" | "scriptures";
  onRegisterSubmit?: (submitFn: (() => Promise<boolean>) | null) => void;
}

export const Predial: React.FC<Props> = ({ type, doc, onRegisterSubmit }) => {
  const [documentType, setDocumentType] = useState<string | undefined>(
    "predial",
  );
  const [files, setFiles] = useState<UploadFile[]>([]);

  const dispatch = useAppDispatch();

  const { activeRequirement, activeVault, activeTab } = useAppSelector(
    (state) => state.vault,
  );
  const uploadMutation = useUploadFileToVault(activeVault?.id);
  const updateFormValuesMutation = useUpdateVaultFormValues(activeVault?.id);

  const enabledInputs =
    activeRequirement?.status === Status.PendingUpload ||
    activeRequirement?.status === Status.Invalid;

  const label = labelPredialOptions[doc][type];
  const isScripturesMode = doc === "scriptures";
  const maxFileSizeMB =
    documentType === "scriptures"
      ? LARGE_DOCUMENT_MAX_FILE_SIZE_MB
      : PREDIAL_MAX_FILE_SIZE_MB;

  const documentExamples = [
    { path: "/documents/predial-1.jpeg", description: "Predial" },
    { path: "/documents/predial-2.jpeg", description: "Predial" },
  ];

  const initFormValues = useCallback(() => {
    if (!activeRequirement?.form_values) return;

    const values = activeRequirement.form_values || [];
    const documentTypeValue =
      values.find((item: FormValue) => item.key === "document_type")?.value ||
      "predial";

    setDocumentType(documentTypeValue);
  }, [activeRequirement?.form_values]);

  useEffect(() => {
    // Reset files when requirement changes
    setFiles([]);
    initFormValues();
  }, [activeRequirement?.id, initFormValues]);

  // Clear files when document type changes
  const handleDocumentTypeChange = (newType: string) => {
    if (newType !== documentType) {
      setFiles([]);
    }
    setDocumentType(newType);
  };

  // Get the label for the current document type
  const getUploadLabel = () => {
    if (!isScripturesMode) {
      return "Predial del año en curso";
    }
    return documentType === "scriptures"
      ? "Escrituras del inmueble (PDF)"
      : "Predial del año en curso";
  };

  const handleSubmit = async (): Promise<boolean> => {
    if (files.length === 0) {
      toast.error("Por favor sube un archivo");
      return false;
    }

    const file = files[0]?.originFileObj;
    const formFiles = [file];
    const documentTypeKey = "predial";
    const requirementFiles = activeRequirement?.files || [];
    const formNFiles = 1;
    const successMessage = isScripturesMode
      ? documentType === "scriptures"
        ? "Escrituras subidas correctamente."
        : "Predial subido correctamente."
      : "Predial subido correctamente.";

    const messageApi = {
      success: (msg: string) => toast.success(msg),
      error: (msg: string) => toast.error(msg),
    };

    // In scriptures mode, include document_type in form values
    const reqFormValues: FormValue[] | undefined =
      isScripturesMode && documentType
        ? activeRequirement?.form_values?.map((formItem) => ({
            ...formItem,
            value:
              formItem.key === "document_type" ? documentType : formItem.value,
          }))
        : undefined;

    try {
      await uploadFileRequirement({
        uploadMutateAsync: uploadMutation.mutateAsync,
        updateFormValuesMutateAsync: updateFormValuesMutation.mutateAsync,
        dispatch,
        requirementFiles,
        documentType: documentTypeKey,
        uploadPolicyDocumentType: documentType,
        formNFiles,
        formFiles,
        formValues: {},
        activeRequirement,
        activeVault,
        activeTab,
        successMessage,
        messageApi,
        ...(reqFormValues ? { reqFormValues } : {}),
      });
      setFiles([]);
      return true;
    } catch (error) {
      console.error("Upload failed:", error);
      return false;
    }
  };

  // Register submit function with parent for external triggering
  const submitFnRef = useRef<(() => Promise<boolean>) | undefined>(undefined);
  submitFnRef.current = handleSubmit;

  useEffect(() => {
    if (!onRegisterSubmit) return;
    const fn = async () => submitFnRef.current?.() ?? false;
    onRegisterSubmit(fn);
    return () => onRegisterSubmit(null);
  }, [onRegisterSubmit]);

  // Get tips based on document type
  const getTips = () => {
    const baseTips = [
      "Documento legible y completo",
      "Todas las páginas deben ser visibles",
    ];

    if (documentType === "scriptures") {
      return [
        ...baseTips,
        "Un solo archivo PDF con todas las páginas",
        `Tamaño máximo: ${LARGE_DOCUMENT_MAX_FILE_SIZE_MB} MB`,
      ];
    }

    return [...baseTips, "Recibo del año en curso", "A nombre del propietario"];
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8">
        <p className="text-gray-600">
          Adjunta <span className="font-semibold text-gray-900">{label}</span>
        </p>
      </div>

      {/* Invalid Requirement Reason — shown at the top so the rejection
          message is visible before the user scrolls through the form. */}
      <InvalidRequirementReason requirement={activeRequirement!} />

      {/* Two column layout - stack on mobile, 40/60 split on md+ */}
      <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-6 md:gap-8">
        {/* Left Column - Document Type Selection (only for scriptures mode) */}
        <div className="space-y-6 md:sticky md:top-0 md:self-start">
          {isScripturesMode && (
            <DocumentTypeSelector
              title="Tipo de documento"
              options={predialOrScripturesTypes}
              value={documentType}
              onChange={handleDocumentTypeChange}
              disabled={!enabledInputs}
            />
          )}

          {/* Tips */}
          <div className="space-y-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {isScripturesMode ? "Requisitos" : "Tips de captura"}
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              {getTips().map((tip, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                  {tip}
                </li>
              ))}
            </ul>
            <DocumentExamples urls={documentExamples} />
          </div>
        </div>

        {/* Right Column - File Upload */}
        <div className="space-y-6">
          <div className="w-full">
            {/* Label */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {isScripturesMode
                  ? labelPredialOrScripturesOptions[documentType || "predial"]
                  : "Documento"}
              </span>
            </div>
            <DocumentUploadCard
              name="predial"
              label={getUploadLabel()}
              type="both"
              enableCropper={false}
              maxSizeMB={maxFileSizeMB}
              onFileChange={setFiles}
              value={files}
            />
          </div>

          <UploadProgress
            status={uploadMutation.status}
            progress={uploadMutation.uploadProgress}
            transport={uploadMutation.uploadTransport}
          />

          {/* Uploaded Files */}
          <UploadedFiles />

          {/* Cancel to review (when already submitted) */}
          {!enabledInputs &&
            activeRequirement?.status === Status.PendingReview && (
              <CancelToReview requirement={activeRequirement} />
            )}
        </div>
      </div>
    </div>
  );
};
